import React, { FC, useState, useRef, useCallback, useMemo } from 'react';
import { Button, Checkbox, confirm, Tooltip } from 'src/common';
import {
  AssignToQuestion,
  EditableText,
  QuickActions,
  QuickActionsDivider,
  QuickActionsItem,
  TaskFulfillmentPicker,
  TaskRequirementPicker,
  TaskResponsePicker,
  TaskStatusPicker
} from 'src/shared';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import {
  useBidding,
  useBiddingGroup,
  useDeleteBidGroup,
  useGroupExpand,
  useTasks,
  useUpdateBidGroup
} from 'src/models/bids/BidFull/hooks';
import modalStyles from 'src/common/Modal/index.module.scss';
import { faAngleDown, faPen, faTrashCan, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useClickAway, useKey } from 'react-use';
import { BoxFieldString } from 'src/models/procurements';
import { IBoxFieldString } from 'src/models/procurements/Tender/types';
import { TasksData } from 'src/models/bids/BidFull/context';
import { useDeleteBidTasks } from 'src/models/bids/BidTask/hooks';

interface IProps {
  groupId: string;
}
export const TaskGroupTitle: FC<IProps> = ({ groupId }) => {
  const { t } = useTranslation();
  const { isExpanded, toggle } = useGroupExpand();
  const [isEditing, setEditing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useBidding();
  const bidId = data?.id;
  const group = useBiddingGroup(groupId);
  const title = group?.title ?? '';
  const [updateGroup] = useUpdateBidGroup();
  const [deleteGroup] = useDeleteBidGroup();
  const { selectedTasks, manual, generated, setSelectedTaskIds } = useTasks();
  const [deleteTasks] = useDeleteBidTasks();

  const selectedTaskIds = useMemo(() => selectedTasks.map(item => item.id), [selectedTasks]);

  const getTaskIds = useCallback((tasks: TasksData) => {
    const { data, offset, limit } = tasks;

    if (data && data.data) {
      const taskIdsWithFilters = data.data.tasks.map(item => (item ? item.id : null));

      return taskIdsWithFilters.slice(offset, offset + limit).filter(Boolean) as string[];
    }

    return [];
  }, []);

  const onSelectAllTasks = useCallback(
    e => {
      const isSelected = e.target.checked;
      if (isSelected) {
        const generatedTaskIds = getTaskIds(generated);
        const manualTaskIds = getTaskIds(manual);
        const taskIdsSet = new Set([...selectedTaskIds, ...generatedTaskIds, ...manualTaskIds]);

        setSelectedTaskIds(Array.from(taskIdsSet));
      } else {
        setSelectedTaskIds([]);
      }
    },
    [generated, manual, selectedTaskIds, getTaskIds, setSelectedTaskIds]
  );

  const enterEditing = useCallback(() => {
    setEditing(() => true);
  }, []);
  const exitEditing = useCallback(() => {
    setEditing(() => false);
  }, []);
  useKey('Escape', () => exitEditing());
  useClickAway(ref, e => {
    e.stopPropagation();
    e.preventDefault();
    exitEditing();
  });
  const onChange = useCallback(
    (value: IBoxFieldString) => {
      bidId &&
        updateGroup({
          bidId,
          groupId,
          title: value.string ?? ''
        });
      exitEditing();
    },
    [bidId, exitEditing, groupId, updateGroup]
  );

  const onDeleteGroup = useCallback(() => bidId && deleteGroup({ bidId, groupId }), [bidId, deleteGroup, groupId]);
  const onShowDeleteGroup = useCallback(() => {
    confirm.confirm({
      title: t('BiddingTool.deleteGroup'),
      okText: t('Common.delete'),
      content: t('BiddingTool.deleteGroupWarning'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk: onDeleteGroup
    });
  }, [onDeleteGroup, t]);
  const isDisabled = useMemo(() => /*group.questions.length === 0*/ false, []);

  const isAllSelected = useMemo(() => {
    const isAllManualSelected = getTaskIds(manual).every(item => selectedTaskIds.includes(item));
    const isAllGeneratedSelected = getTaskIds(generated).every(item => selectedTaskIds.includes(item));

    return isAllManualSelected && isAllGeneratedSelected;
  }, [manual, generated, selectedTaskIds, getTaskIds]);

  const onRemoveTasks = useCallback(() => {
    confirm.confirm({
      title: t('BiddingTool.BulkActions.deleteTasks'),
      okText: t('Common.delete'),
      content: t('BiddingTool.BulkActions.deleteTasksWarning'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk() {
        if (groupId) {
          deleteTasks({
            questionIds: selectedTasks.map(item => item.id),
            groupId,
            mode: 'bulk'
          });

          generated.setOffset(0);
          manual.setOffset(0);
        }
      }
    });
  }, [selectedTasks, t, groupId, deleteTasks, manual, generated]);

  return group ? (
    <div className={styles.heading}>
      {selectedTasks.length > 0 ? (
        <>
          <div className={styles.headingBgc} />
          <Checkbox
            indeterminate={!!selectedTasks.length && !isAllSelected}
            onChange={onSelectAllTasks}
            checked={isAllSelected}
            className={styles.checkbox}
          />
          <span className={styles.toolsLabel}>{`${selectedTasks.length} ${
            selectedTasks.length === 1
              ? t('BiddingTool.BulkActions.selectedSingular')
              : t('BiddingTool.BulkActions.selectedPlural')
          }`}</span>
          <div className={styles.fulfillment}>
            <TaskFulfillmentPicker groupId={groupId} questions={selectedTasks} mode="bulk" />
          </div>
          <div className={styles.requirementType}>
            <TaskRequirementPicker groupId={groupId} questions={selectedTasks} />
          </div>
          <div className={styles.responseType}>
            <TaskResponsePicker groupId={groupId} questions={selectedTasks} mode="bulk" />
          </div>
          <div className={styles.status}>
            <TaskStatusPicker groupId={groupId} questions={selectedTasks} mode="bulk" label={t('Common.status')} />
          </div>
          {bidId && (
            <AssignToQuestion
              questions={selectedTasks}
              wsId={data.workspaceId}
              groupId={groupId}
              bidId={bidId}
              assignToMode="bulk"
            />
          )}
          <Button type={'text'} icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => onRemoveTasks()} />
        </>
      ) : (
        <>
          <div
            className={classNames(styles.headingColorBgc, { [styles.isDisabled]: isDisabled })}
            style={{ backgroundColor: group.color }}
          />
          <div className={styles.headingBgc} />
          {isEditing ? (
            <EditableText
              className={styles.title}
              containerRef={ref}
              onChange={onChange}
              onFinish={exitEditing}
              field={new BoxFieldString(title, title)}
              autoSize={true}
              autoFocus
            />
          ) : (
            <Button
              className={classNames(styles.button, { [styles.isDisabled]: isDisabled })}
              disabled={isDisabled}
              icon={
                <FontAwesomeIcon
                  icon={faAngleDown}
                  className={classNames(styles.icon, { [styles.isExpanded]: isExpanded })}
                />
              }
              type={'text'}
              onClick={toggle}
            >
              {!!group.title ? (
                <Tooltip title={group.title}>
                  <span className={styles.label}>
                    {group.title}
                    <span className={styles.groupStat}>{` (${group.totalDoneTasks}/${group.totalTasks})`}</span>
                  </span>
                </Tooltip>
              ) : (
                t('BiddingTool.newGroupPlaceholder')
              )}
            </Button>
          )}
          <QuickActions>
            <QuickActionsItem
              key={'renameGroup'}
              onClick={enterEditing}
              title={t('BiddingTool.renameGroup')}
              icon={faPen}
            />
            <QuickActionsDivider />
            <QuickActionsItem
              onClick={onShowDeleteGroup}
              title={t('BiddingTool.deleteGroup')}
              key={'deleteGroup'}
              icon={faTrashCan}
              danger
            />
          </QuickActions>
        </>
      )}
    </div>
  ) : null;
};

export default TaskGroupTitle;
